import classNames from "classnames";
import React, { useMemo } from "react";
import { FormatedValue } from "components/FormatedValue";
import { BalanceGroupContentProps, BalanceProps } from "./types";
import { useBalance } from "./useBalance";

import "./styles.scss";

const unconfortableWidth = 282 * (2 / 3);
const unconfortableWidthMin = unconfortableWidth / 1.3;
const mediumWidth = 180 * (2 / 3);
const mediumWidthMin = mediumWidth / 1.3;
const averageWidth = 147 * (2 / 3);
const averageWidthMin = averageWidth / 1.3;
const perfectWidth = 100 * (2 / 3);
const perfectWidthMin = perfectWidth / 1.3;

const relation = {
  unconfortable: 0.5,
  medium: 0.3,
  average: 0.2,
  perfect: 0.1,
};

const calculateWidth = (
  percentage: number | undefined,
  width: number,
  minimumWidth: number,
  relation: number,
) => {
  if (percentage) {
    const parsed = Math.min(percentage, relation);

    return (width - minimumWidth) * parsed + minimumWidth;
  }

  return 0;
};

const BalanceGroupContent: React.FC<BalanceGroupContentProps> = ({
  group,
  total,
  color,
  width,
}) => {
  return (
    <div
      style={{
        color: color ? `var(${color})` : "white",
        width: width,
      }}
      className="balance__text truncate capitalize"
    >
      {group.name}
      <br />
      <FormatedValue
        value={((group.value / total) * 100).toString()}
        precision={2}
      />
      %
    </div>
  );
};

export const Balance: React.FC<BalanceProps> = ({ balanceGroups }) => {
  const { loop, setLoop, idle, setIdle, totalBalance } = useBalance({
    balanceGroups,
  });

  const {
    unconfortableWidthCalculated,
    mediumWidthCalculated,
    averageWidthCalculated,
    perfectWidthCalculated,
  } = useMemo(() => {
    return {
      unconfortableWidthCalculated: calculateWidth(
        balanceGroups[0]?.percentage,
        unconfortableWidth,
        unconfortableWidthMin,
        relation.unconfortable,
      ),
      mediumWidthCalculated: calculateWidth(
        balanceGroups[1]?.percentage,
        mediumWidth,
        mediumWidthMin,
        relation.medium,
      ),
      averageWidthCalculated: calculateWidth(
        balanceGroups[2]?.percentage,
        averageWidth,
        averageWidthMin,
        relation.average,
      ),
      perfectWidthCalculated: calculateWidth(
        balanceGroups[3]?.percentage,
        perfectWidth,
        perfectWidthMin,
        relation.perfect,
      ),
    };
  }, [balanceGroups]);

  return (
    <div className="flex items-center justify-center">
      <div className="balance">
        <div
          onAnimationEnd={() =>
            setIdle((state) => ({ ...state, unconfortable: true }))
          }
          style={{
            width:
              balanceGroups && idle.unconfortable
                ? unconfortableWidthCalculated
                : undefined,
            height:
              balanceGroups && idle.unconfortable
                ? unconfortableWidthCalculated
                : undefined,
            borderWidth:
              balanceGroups && balanceGroups.length < 1 ? 0 : undefined,
          }}
          className={classNames(
            idle.unconfortable && "balance__idle",
            !loop && "balance__loaded-ball",
            loop && "balance__loading-ball",
            "unconfortable",
            loop && "loop",
            "ball",
          )}
        >
          {balanceGroups && balanceGroups[0] && idle.unconfortable && (
            <BalanceGroupContent
              group={balanceGroups[0]}
              total={totalBalance}
              width={unconfortableWidthCalculated - 5}
              color="--milkylb-color-secondary-400"
            />
          )}
        </div>
        <div
          onAnimationEnd={() =>
            setIdle((state) => ({ ...state, medium: true }))
          }
          style={{
            width:
              balanceGroups && idle.medium ? mediumWidthCalculated : undefined,
            height:
              balanceGroups && idle.medium ? mediumWidthCalculated : undefined,
            borderWidth:
              balanceGroups && balanceGroups.length < 2 ? 0 : undefined,
          }}
          className={classNames(
            idle.medium && "balance__idle",
            !loop && "balance__loaded-ball",
            loop && "balance__loading-ball",
            "medium",
            loop && "loop",
            "ball",
          )}
        >
          {balanceGroups && balanceGroups[1] && idle.medium && (
            <BalanceGroupContent
              group={balanceGroups[1]}
              width={mediumWidthCalculated - 5}
              total={totalBalance}
            />
          )}
        </div>
        <div
          onAnimationEnd={() =>
            setIdle((state) => ({ ...state, average: true }))
          }
          style={{
            width:
              balanceGroups && idle.average
                ? averageWidthCalculated
                : undefined,
            height:
              balanceGroups && idle.average
                ? averageWidthCalculated
                : undefined,
            borderWidth:
              balanceGroups && balanceGroups.length < 3 ? 0 : undefined,
          }}
          className={classNames(
            idle.average && "balance__idle",
            !loop && "balance__loaded-ball",
            loop && "balance__loading-ball",
            "average",
            loop && "loop",
            "ball",
          )}
        >
          {balanceGroups && balanceGroups[2] && idle.average && (
            <BalanceGroupContent
              group={balanceGroups[2]}
              width={averageWidthCalculated - 5}
              total={totalBalance}
            />
          )}
        </div>
        <div
          onAnimationEnd={() =>
            setIdle((state) => ({ ...state, perfect: true }))
          }
          style={{
            width:
              balanceGroups && idle.perfect
                ? perfectWidthCalculated
                : undefined,
            height:
              balanceGroups && idle.perfect
                ? perfectWidthCalculated
                : undefined,
            borderWidth:
              balanceGroups && balanceGroups.length < 4 ? 0 : undefined,
          }}
          className={classNames(
            idle.perfect && "balance__idle",
            !loop && "balance__loaded-ball",
            loop && "balance__loading-ball",
            "perfect",
            loop && "loop",
            "ball",
          )}
          onAnimationIteration={() => {
            if (balanceGroups) {
              setLoop(false);
            }
          }}
        >
          {balanceGroups && balanceGroups[3] && idle.perfect && (
            <BalanceGroupContent
              width={perfectWidthCalculated - 15}
              group={balanceGroups[3]}
              total={totalBalance}
            />
          )}
        </div>
      </div>
    </div>
  );
};
